import { useCallback, useMemo, useState } from "react"

import {
  COMPARE_BY_OPTION,
  CUSTOM_UNASSIGNED_KEY,
  useCompareByFeature,
  type CompareBy,
  type CompareByOption,
} from "@/run-detail/compare-by"

import type { RunRecord } from "@/types/api/RunRecord"
import type { RunRowLike } from "@/types/RunRow"

import { EMPTY_CUSTOM_DIMENSIONS_CONFIG } from "../utils/reportDetailRouteState"
import {
  DEFAULT_REPORT_SPLIT_SIZES,
  type CustomDimensionsConfig,
} from "../utils/reportViewConfig"
import {
  buildCompareByDisplayLabels,
  buildSimulationOutputRunIdMap,
  getPresentMetricIds,
  type BuildCompareByDisplayLabelsParams,
  type ReportRunsById,
} from "../utils/reportWorkspaceData"

type WorkspaceRunSummary = Partial<
  Pick<
    RunRecord,
    | "agent_id"
    | "created_at"
    | "customer_metadata"
    | "display_name"
    | "id"
    | "model_type"
    | "persona_id_id"
    | "status"
    | "tags"
    | "test_set_id"
  >
>

interface UseReportWorkspaceStateParams {
  agentsById: BuildCompareByDisplayLabelsParams["agentsById"]
  compareBy: CompareByOption
  compareByColorOverrides: Record<string, string>
  compareByMetadataKey: string | null
  customDimensions?: CustomDimensionsConfig
  customDimensionId?: string | null
  multiRunMetadata:
    | { runs?: Array<WorkspaceRunSummary | null> }
    | null
    | undefined
  personasById: BuildCompareByDisplayLabelsParams["personasById"]
  preferTestCaseDescription?: boolean
  runRows: RunRowLike[] | null | undefined
  runsById: ReportRunsById
  setCompareByColorOverrides: (value: Record<string, string>) => void
  /** tag name → color; forwarded to useCompareByFeature so tag groups
   *  render in the same color chip the user picked for the tag. Only used
   *  when compareBy === "tag". */
  tagColorMap?: Record<string, string | null> | null
}

export function useReportWorkspaceState({
  agentsById,
  compareBy,
  compareByColorOverrides,
  compareByMetadataKey,
  customDimensions = EMPTY_CUSTOM_DIMENSIONS_CONFIG,
  customDimensionId = null,
  multiRunMetadata,
  personasById,
  preferTestCaseDescription = true,
  runRows,
  runsById,
  setCompareByColorOverrides,
  tagColorMap = null,
}: UseReportWorkspaceStateParams) {
  const [hoveredRunId, setHoveredRunId] = useState<string | null>(null)
  const [selectedMetricId, setSelectedMetricIdRaw] = useState<string | null>(
    null,
  )
  // A selected plot drives the right-pane editor; a selected metric drives the
  // metric drill-in. They are mutually exclusive — selecting one clears the
  // other so the right pane shows a single thing.
  const [selectedPlotId, setSelectedPlotIdRaw] = useState<string | null>(null)
  const [selectedSimulationOutputIds, setSelectedSimulationOutputIds] =
    useState<string[]>([])
  const [splitSizes, setSplitSizes] = useState<number[]>(
    DEFAULT_REPORT_SPLIT_SIZES,
  )

  const setSelectedMetricId = useCallback((id: string | null) => {
    setSelectedMetricIdRaw(id)
    if (id !== null) {
      setSelectedPlotIdRaw(null)
    }
  }, [])
  const setSelectedPlotId = useCallback((id: string | null) => {
    setSelectedPlotIdRaw(id)
    if (id !== null) {
      setSelectedMetricIdRaw(null)
    }
  }, [])

  const simOutputToRunId = useMemo(
    () => buildSimulationOutputRunIdMap(runRows),
    [runRows],
  )

  const presentMetricIds = useMemo(
    () => getPresentMetricIds(runRows),
    [runRows],
  )

  const activeCustomDimension = useMemo(() => {
    if (!customDimensionId) {
      return null
    }
    return (
      customDimensions.dimensions.find((d) => d.id === customDimensionId) ??
      null
    )
  }, [customDimensions, customDimensionId])

  const {
    compareByColors,
    compareByValueToColor,
    distinctCompareByValues,
    setCompareByColor,
  } = useCompareByFeature({
    compareBy,
    compareByMetadataKey,
    multiRunMetadata: multiRunMetadata ?? null,
    combinedRunRows: runRows ?? null,
    agentsById,
    personasById,
    compareByColorOverrides,
    setCompareByColorOverrides,
    activeCustomDimension,
    tagColorMap,
  })

  const customGroupBySimId = useMemo<Record<string, string>>(() => {
    if (!activeCustomDimension) {
      return {}
    }
    const map: Record<string, string> = {}
    for (const group of activeCustomDimension.groups) {
      for (const simId of group.simulationIds) {
        map[simId] = group.id
      }
    }
    return map
  }, [activeCustomDimension])

  const customGroupDisplayLabels = useMemo<Record<string, string>>(() => {
    const labels: Record<string, string> = {
      [CUSTOM_UNASSIGNED_KEY]: "Unassigned",
    }
    if (activeCustomDimension) {
      for (const group of activeCustomDimension.groups) {
        labels[group.id] = group.name
      }
    }
    return labels
  }, [activeCustomDimension])

  // Tags live on the RunRecord (evals_run.tags), so we index by run id and
  // let the compare-by machinery look each row's tags up via row.run_id.
  // Only computed when dimension === "tag" so unrelated compareBy switches
  // don't churn the memoized config.
  const tagsByRunId = useMemo<Record<string, string[]> | undefined>(() => {
    if (compareBy !== "tag") {
      return undefined
    }
    const runs = multiRunMetadata?.runs ?? []
    const map: Record<string, string[]> = {}
    for (const run of runs) {
      if (!run?.id) {
        continue
      }
      const tags = run.tags ?? []
      if (tags.length > 0) {
        map[run.id] = tags
      }
    }
    return map
  }, [compareBy, multiRunMetadata?.runs])

  const compareByConfigForGroup = useMemo<CompareBy | null>(
    () =>
      compareBy === COMPARE_BY_OPTION.NONE
        ? null
        : {
            dimension: compareBy,
            metadataKey:
              compareBy === "metadata"
                ? (compareByMetadataKey ?? undefined)
                : undefined,
            colors: {},
            customGroupBySimId:
              compareBy === "custom" ? customGroupBySimId : undefined,
            tagsByRunId: compareBy === "tag" ? tagsByRunId : undefined,
          },
    [compareBy, compareByMetadataKey, customGroupBySimId, tagsByRunId],
  )

  const compareByDisplayLabels = useMemo(() => {
    const baseLabels = buildCompareByDisplayLabels({
      agentsById,
      compareByConfigForGroup,
      personasById,
      preferTestCaseDescription,
      runRows,
      runsById,
    })
    if (compareBy === "custom") {
      return { ...baseLabels, ...customGroupDisplayLabels }
    }
    return baseLabels
  }, [
    agentsById,
    compareBy,
    compareByConfigForGroup,
    customGroupDisplayLabels,
    personasById,
    preferTestCaseDescription,
    runRows,
    runsById,
  ])

  return {
    activeCustomDimension,
    compareByColors,
    compareByConfigForGroup,
    compareByDisplayLabels,
    compareByValueToColor,
    customGroupBySimId,
    distinctCompareByValues,
    hoveredRunId,
    presentMetricIds,
    selectedMetricId,
    selectedPlotId,
    selectedSimulationOutputIds,
    setCompareByColor,
    setHoveredRunId,
    setSelectedMetricId,
    setSelectedPlotId,
    setSelectedSimulationOutputIds,
    setSplitSizes,
    simOutputToRunId,
    splitSizes,
  }
}
